// Goals service for CareerCatalyst
// Handles goals and job posting management operations

import { api, API_CONFIG } from './api';
import type {
  Goal,
  JobPosting,
  GapRemediationSessionResponse,
  GeneratedResume,
  ResumeGenerationRequest,
  ResumeVersionSummary,
  ResumeEditHistory,
  PersonalAdvocateStatus,
  PersonalAdvocateCreateRequest,
  PersonalAdvocateUpdateRequest,
  GoalUsageResponse,
} from '../types';

// Goals request types
export interface CreateGoalRequest {
  title: string;
  description: string;
  target_role: string;
  target_company?: string;
  timeline: string;
  status?: 'active' | 'completed' | 'paused';
}

export interface UpdateGoalRequest {
  title?: string;
  description?: string;
  target_role?: string;
  target_company?: string;
  timeline?: string;
  status?: 'active' | 'completed' | 'paused';
}

export interface CreateJobPostingRequest {
  title: string;
  company: string;
  description: string;
  requirements: string[];
  location?: string;
  salary_range?: string;
  posted_date?: string;
  source_url?: string;
}

// Request for processing validated job content (matches backend ProcessValidatedContentRequest)
export interface ProcessValidatedContentRequest {
  content: string;
  source_type: 'uploaded' | 'scraped' | 'pasted';
  source_url?: string;
}

export interface UpdateJobPostingRequest {
  title?: string;
  company?: string;
  description?: string;
  requirements?: string[];
  location?: string;
  salary_range?: string;
  posted_date?: string;
  source_url?: string;
}

export interface GoalListResponse {
  items: Goal[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface JobPostingListResponse {
  items: JobPosting[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface GoalAnalysisResponse {
  goal_id: string;
  feasibility_score: number;
  timeline_assessment: string;
  required_skills: string[];
  recommended_experiences: string[];
  potential_challenges: string[];
  success_probability: number;
  action_plan: Array<{
    phase: string;
    duration: string;
    objectives: string[];
    milestones: string[];
  }>;
}

// Job content validation types
export interface ValidateJobContentRequest {
  content?: string;
  url?: string;
  source: 'url' | 'paste' | 'upload';
}

export interface ValidateJobContentResponse {
  is_valid: boolean;
  content_type: string;
  message: string;
  preview?: string;
  detected_title?: string;
  detected_company?: string;
  scraped_content?: string;
}

// GoalsService class
class GoalsService {
  // Goals management

  /**
   * Upload job posting file and create goal
   * Handles file upload (PDF, DOCX, TXT) and creates goal with job posting
   */
  async uploadJobPosting(file: File): Promise<Goal> {
    const formData = new FormData();
    formData.append('file', file);

    return api.upload<Goal>('/goals/upload', formData);
  }

  /**
   * Validate job content before creating a goal
   * Used to ensure scraped/pasted content is actually a job posting
   * This is FREE and does not consume credits
   */
  async validateJobContent(request: ValidateJobContentRequest): Promise<ValidateJobContentResponse> {
    return api.post<ValidateJobContentResponse>('/goals/validate-job-content', request);
  }

  /**
   * Validate a job posting file before creating a goal
   * Extracts text from file and validates it's a job posting
   * This is FREE and does not consume credits
   */
  async validateJobFile(file: File): Promise<ValidateJobContentResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return api.upload<ValidateJobContentResponse>('/goals/validate-job-file', formData);
  }

  /**
   * Process validated job content into Goal + JobPosting
   * Call this AFTER validateJobContent returns is_valid=true
   * This consumes 1 job credit and creates the full job analysis
   */
  async processValidatedContent(data: ProcessValidatedContentRequest): Promise<Goal> {
    return api.post<Goal>('/goals/process-validated-content', data);
  }

  /**
   * Get all goals for current user
   */
  async getGoals(page: number = 1, size: number = 50): Promise<Goal[] | GoalListResponse> {
    // Backend currently returns an array directly, not a paginated response
    return api.get<Goal[]>(`/goals/?page=${page}&size=${size}`);
  }

  /**
   * Get goal by ID
   */
  async getGoalById(goalId: string): Promise<Goal> {
    return api.get<Goal>(`/goals/${goalId}`);
  }

  /**
   * Create new goal
   */
  async createGoal(goalData: CreateGoalRequest): Promise<Goal> {
    return api.post<Goal>('/goals/', goalData);
  }

  /**
   * Update goal
   */
  async updateGoal(goalId: string, goalData: UpdateGoalRequest): Promise<Goal> {
    return api.patch<Goal>(`/goals/${goalId}`, goalData);
  }

  /**
   * Delete goal
   */
  async deleteGoal(goalId: string): Promise<void> {
    await api.delete<void>(`/goals/${goalId}`);
  }

  /**
   * Get goals by status
   */
  async getGoalsByStatus(status: 'active' | 'completed' | 'paused', page: number = 1, size: number = 50): Promise<GoalListResponse> {
    return api.get<GoalListResponse>(`/goals/status/${status}?page=${page}&size=${size}`);
  }

  /**
   * Analyze goal feasibility and create action plan
   */
  async analyzeGoal(goalId: string): Promise<GoalAnalysisResponse> {
    return api.post<GoalAnalysisResponse>(`/goals/${goalId}/analyze`);
  }

  /**
   * Update goal progress
   */
  async updateGoalProgress(goalId: string, progress: {
    completion_percentage: number;
    notes?: string;
    milestones_completed?: string[];
  }): Promise<Goal> {
    return api.patch<Goal>(`/goals/${goalId}/progress`, progress);
  }

  // Job Postings management

  /**
   * Get all job postings for current user
   */
  async getJobPostings(page: number = 1, size: number = 50): Promise<JobPostingListResponse> {
    return api.get<JobPostingListResponse>(`/goals/job-postings?page=${page}&size=${size}`);
  }

  /**
   * Get job posting by ID
   */
  async getJobPostingById(jobPostingId: string): Promise<JobPosting> {
    return api.get<JobPosting>(`/goals/job-postings/${jobPostingId}`);
  }

  /**
   * Create new job posting
   * @deprecated This endpoint no longer exists in the backend (causes 405 error).
   * Use validateJobContent() + processValidatedContent() instead for the two-step validation flow.
   */
  async createJobPosting(jobPostingData: CreateJobPostingRequest): Promise<JobPosting> {
    throw new Error('createJobPosting is deprecated. Use validateJobContent() + processValidatedContent() instead.');
  }

  /**
   * Update job posting
   */
  async updateJobPosting(jobPostingId: string, jobPostingData: UpdateJobPostingRequest): Promise<JobPosting> {
    return api.patch<JobPosting>(`/goals/job-postings/${jobPostingId}`, jobPostingData);
  }

  /**
   * Delete job posting
   */
  async deleteJobPosting(jobPostingId: string): Promise<void> {
    await api.delete<void>(`/goals/job-postings/${jobPostingId}`);
  }

  /**
   * Link job posting to goal
   * @deprecated This endpoint no longer exists in the backend.
   * processValidatedContent() now handles linking automatically.
   */
  async linkJobPostingToGoal(goalId: string, jobPostingId: string): Promise<{
    goal_id: string;
    job_posting_id: string;
    linked_at: string;
  }> {
    throw new Error('linkJobPostingToGoal is deprecated. processValidatedContent() handles linking automatically.');
  }

  /**
   * Unlink job posting from goal
   * @deprecated Legacy function - no longer supported.
   */
  async unlinkJobPostingFromGoal(goalId: string, jobPostingId: string): Promise<void> {
    throw new Error('unlinkJobPostingFromGoal is deprecated.');
  }

  /**
   * Get job postings linked to a goal
   */
  async getJobPostingsForGoal(goalId: string, page: number = 1, size: number = 50): Promise<JobPostingListResponse> {
    return api.get<JobPostingListResponse>(`/goals/${goalId}/job-postings?page=${page}&size=${size}`);
  }

  /**
   * Search job postings
   */
  async searchJobPostings(query: string, filters?: {
    company?: string;
    location?: string;
    salary_min?: number;
    salary_max?: number;
  }, page: number = 1, size: number = 50): Promise<JobPostingListResponse> {
    const params = new URLSearchParams({
      q: query,
      page: page.toString(),
      size: size.toString(),
    });

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, value.toString());
        }
      });
    }

    return api.get<JobPostingListResponse>(`/goals/job-postings/search?${params.toString()}`);
  }

  /**
   * Import job posting from URL
   */
  async importJobPostingFromUrl(url: string): Promise<JobPosting> {
    return api.post<JobPosting>('/goals/job-postings/import', { url });
  }

  /**
   * Analyze match between user profile and job posting
   */
  async analyzeJobMatch(jobPostingId: string): Promise<{
    match_percentage: number;
    matching_skills: string[];
    missing_skills: string[];
    experience_alignment: number;
    recommendations: string[];
  }> {
    return api.post(`/goals/job-postings/${jobPostingId}/analyze-match`);
  }

  /**
   * Start gap refutation session for a goal
   * Connects to backend endpoint that creates chat session with Gap Navigator
   * @param gapId - ID of the gap to refute
   * @param goalId - ID of the goal context
   * @returns Session data with chat ID, match percentages, and gap details
   */
  async startGapRefutation(gapId: string, goalId: string): Promise<GapRemediationSessionResponse> {
    // Call backend API
    const response = await api.post<any>(`/gaps/${gapId}/start-remediation-for-goal/${goalId}`);

    // Transform backend gap_type to frontend type
    // Backend uses "gap_type" in GapResponse schema (MVP compatible, Issue #42)
    // Frontend expects "type" in Gap interface for TypeScript type safety
    if (response.gap && response.gap.gap_type !== undefined) {
      response.gap.type = response.gap.gap_type;
      delete response.gap.gap_type;
    }

    return response as GapRemediationSessionResponse;
  }

  // ========================================================================
  // RESUME GENERATION METHODS
  // ========================================================================

  /**
   * Generate truthful, optimized resume for a goal using Gemini
   *
   * @param goalId - Goal ID to generate resume for
   * @param request - Optional generation parameters (notes, regenerate flag)
   * @returns Complete GeneratedResume with structured content, markdown, and source attributions
   */
  async generateResume(
    goalId: string,
    request?: ResumeGenerationRequest
  ): Promise<GeneratedResume> {
    return api.post<GeneratedResume>(`/goals/${goalId}/generate-resume`, request || {});
  }

  /**
   * Get the latest generated resume for a goal
   *
   * @param goalId - Goal ID
   * @returns Latest GeneratedResume or null if none exists
   */
  async getLatestResume(goalId: string): Promise<GeneratedResume | null> {
    try {
      return await api.get<GeneratedResume>(`/goals/${goalId}/resume`);
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get all resume versions for a goal
   *
   * @param goalId - Goal ID
   * @returns List of resume version summaries (newest first)
   */
  async getResumeVersions(goalId: string): Promise<ResumeVersionSummary[]> {
    return api.get<ResumeVersionSummary[]>(`/goals/${goalId}/resumes`);
  }

  /**
   * Get usage stats for a specific goal
   *
   * @param goalId - Goal ID
   * @returns Usage statistics (messages, generations, regeneration availability)
   */
  async getGoalUsage(goalId: string): Promise<GoalUsageResponse> {
    return api.get<GoalUsageResponse>(`/goals/${goalId}/usage`);
  }

  /**
   * Get specific resume version by ID
   *
   * @param goalId - Goal ID
   * @param resumeId - Resume version ID
   * @returns Complete GeneratedResume
   */
  async getResumeById(goalId: string, resumeId: string): Promise<GeneratedResume> {
    return api.get<GeneratedResume>(`/goals/${goalId}/resume/${resumeId}`);
  }

  /**
   * Get edit history for a resume
   *
   * @param goalId - Goal ID
   * @param resumeId - Resume ID
   * @returns List of edit history entries (newest first)
   */
  async getResumeEditHistory(goalId: string, resumeId: string): Promise<ResumeEditHistory[]> {
    return api.get<ResumeEditHistory[]>(`/goals/${goalId}/resume/${resumeId}/edits`);
  }

  /**
   * Download resume in specified format
   *
   * @param goalId - Goal ID
   * @param resumeId - Resume ID
   * @param format - Export format ('pdf', 'txt', 'markdown')
   * @param style - PDF style preset ('compact', 'professional', 'balanced', 'spacious')
   *                Only applies when format is 'pdf'. Defaults to 'professional'.
   * @returns Blob containing the resume file
   */
  async downloadResume(
    goalId: string,
    resumeId: string,
    format: 'pdf' | 'txt' | 'markdown' = 'pdf',
    style?: 'compact' | 'professional' | 'balanced' | 'spacious'
  ): Promise<Blob> {
    // Build URL with format and optional style (for PDF only)
    let url = `${API_CONFIG.BASE_URL}/goals/${goalId}/resume/${resumeId}/export?format=${format}`;
    if (format === 'pdf' && style) {
      url += `&style=${style}`;
    }

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Failed to download resume: ${response.statusText}`);
    }

    return response.blob();
  }

  // ========================================================================
  // PERSONAL ADVOCATE METHODS (Owner Operations - Authenticated)
  // ========================================================================

  /**
   * Enable Personal Advocate for a goal
   *
   * Creates a shareable link that allows employers to view the resume and
   * chat with an AI advocate about the candidate.
   *
   * @param goalId - Goal ID to enable PA for
   * @param includeInResume - Whether to include PA URL in resume header
   * @returns Personal Advocate configuration with access URL and analytics
   */
  async enablePersonalAdvocate(
    goalId: string,
    includeInResume: boolean = true
  ): Promise<PersonalAdvocateStatus> {
    const requestData: PersonalAdvocateCreateRequest = {
      include_in_resume: includeInResume,
    };

    return api.post<PersonalAdvocateStatus>(
      `/goals/${goalId}/personal-advocate`,
      requestData
    );
  }

  /**
   * Get Personal Advocate status and analytics
   *
   * Returns configuration, public URL, and usage analytics for the owner.
   *
   * @param goalId - Goal ID
   * @returns Personal Advocate status with analytics, or null if not enabled
   */
  async getPersonalAdvocateStatus(goalId: string): Promise<PersonalAdvocateStatus | null> {
    try {
      return await api.get<PersonalAdvocateStatus>(`/goals/${goalId}/personal-advocate`);
    } catch (error: any) {
      // Return null if PA not enabled (404)
      // Check error.status (APIError structure) not error.response.status
      if (error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Update Personal Advocate settings
   *
   * Allows owner to activate/deactivate the link or toggle resume inclusion.
   *
   * @param goalId - Goal ID
   * @param settings - Settings to update (is_active, include_in_resume)
   * @returns Updated Personal Advocate configuration
   */
  async updatePersonalAdvocate(
    goalId: string,
    settings: PersonalAdvocateUpdateRequest
  ): Promise<PersonalAdvocateStatus> {
    return api.patch<PersonalAdvocateStatus>(
      `/goals/${goalId}/personal-advocate`,
      settings
    );
  }

  /**
   * Disable Personal Advocate (soft delete)
   *
   * Deactivates the access link while preserving analytics data.
   *
   * @param goalId - Goal ID
   * @returns Success confirmation
   */
  async disablePersonalAdvocate(goalId: string): Promise<{ success: boolean; message: string }> {
    return api.delete(`/goals/${goalId}/personal-advocate`);
  }
}

// Export singleton instance
export const goalsService = new GoalsService();
